import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNoneOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import EventNoteIcon from '@mui/icons-material/EventNote';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useSelector, useDispatch } from "react-redux";
import HomeIcon from '@mui/icons-material/HomeOutlined';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import CarIcon from '@mui/icons-material/DirectionsCarOutlined';
import Message from '@mui/icons-material/MessageOutlined';
import ExitIcon from '@mui/icons-material/LogoutOutlined';
import OfferIcon from '@mui/icons-material/LocalOfferOutlined';
import NotifyIcon from '@mui/icons-material/NotificationsActiveOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import ContactPhoneIcon from '@mui/icons-material/ContactPhoneOutlined';
import ViewListIcon from '@mui/icons-material/ViewListOutlined';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeopleOutlined';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import MoneyIcon from '@mui/icons-material/PaymentsOutlined';
import PhoneIphoneIcon from '@mui/icons-material/SettingsOutlined';
import SosIcon from '@mui/icons-material/SosOutlined';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { calcEst, showEst, optionsRequired } from '../common/sharedFunctions';
import BottomNav from './BottomNav';

// Brand tokens — match landing palette / Inter font
const BRAND = {
  teal: '#4fd1c5',
  tealDark: '#2dd4bf',
  tealLight: 'rgba(79,209,197,0.12)',
  slate950: '#020617',
  slate900: '#0f172a',
  slate800: '#1e293b',
  slate700: '#334155',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate300: '#cbd5e1',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',
  font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
};

const drawerWidth = 264;

export default function ResponsiveDrawer(props) {
  const [notificationCount, setNotificationCount] = useState(0);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { signOff } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const notificationdata = useSelector(state => state.notificationdata);
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const usersdata = useSelector(state => state.usersdata);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [role, setRole] = useState(null);
  const [bellAnchor, setBellAnchor] = useState(null);
  const [seenBellIds, setSeenBellIds] = useState(() => {
    if (typeof window === 'undefined' || !window.localStorage) return new Set();
    const raw = localStorage.getItem('medlift_bell_seen') || '';
    return new Set(raw.split('|').filter(Boolean));
  });
  const [dismissedUntil, setDismissedUntil] = useState(() => {
    if (typeof window === 'undefined' || !window.localStorage) return 0;
    return parseInt(localStorage.getItem('medlift_bell_dismissed_until') || '0', 10);
  });

  // Inject Inter font (matches landing). Once per mount.
  useEffect(() => {
    if (document.querySelector('link[data-medlift-inter]')) return;
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    link.setAttribute('data-medlift-inter', '1');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  const driverNameById = React.useMemo(() => {
    const map = {};
    (usersdata?.staticusers || []).forEach(u => {
      if (u && u.id) map[u.id] = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    });
    return map;
  }, [usersdata?.staticusers]);

  const bellNotifications = React.useMemo(() => {
    const items = [];
    const myUid = auth?.profile?.uid;
    const myRole = auth?.profile?.usertype;
    const symbol = settings?.symbol || '';
    const formatCost = (c) => {
      if (c === undefined || c === null) return '';
      return settings?.swipe_symbol === false ? `${symbol}${c}` : `${c}${symbol}`;
    };
    const refLabel = (b) => `#${b.reference || (b.id ? b.id.slice(-6) : '')}`;
    const dismissedReviews = new Set(((typeof window !== 'undefined' && window.sessionStorage)
      ? (sessionStorage.getItem('medlift_review_dismissed') || '').split(',').filter(Boolean)
      : []));
    const acknowledged = seenBellIds;
    const isReadByTime = (ts) => {
      if (!dismissedUntil || !ts) return false;
      const t = typeof ts === 'number' ? ts : new Date(ts).getTime();
      return !isNaN(t) && t <= dismissedUntil;
    };

    // Build booking lookup by reference / id-tail for enrichment
    const bookingsArr = bookinglistdata?.bookings || [];
    const bookingByRef = {};
    bookingsArr.forEach(b => {
      if (b.reference) bookingByRef[String(b.reference).toUpperCase()] = b;
      if (b.id) bookingByRef[String(b.id).slice(-6).toUpperCase()] = b;
    });
    const refRe = /\b([A-Z0-9]{6})\b/;

    // Status keyword detection (Italian + English) for fallback matching when ref is absent
    const statusKeywords = [
      { re: /(hai raggiunto la destinazione|destination reached|completare il pagamento)/i, target: ['REACHED', 'PENDING', 'PAYMENT_PENDING'] },
      { re: /(ha completato|completed.*trip|trip completed)/i, target: ['COMPLETE', 'PAID'] },
      { re: /(conducente|trasportatore).*(comincia|iniziat|avviat|started)/i, target: ['STARTED'] },
      { re: /(conducente|trasportatore).*(arrivat|arrived)/i, target: ['ARRIVED'] },
      { re: /(accettat|accepted)/i, target: ['ACCEPTED'] },
      { re: /(offerta|bid|offer)/i, target: ['NEW'] },
    ];

    const findLinkedBooking = (msg, title, dated) => {
      const haystack = `${msg} ${title}`;
      const refMatch = haystack.match(refRe);
      if (refMatch) {
        const hit = bookingByRef[refMatch[1]];
        if (hit) return hit;
      }
      if (!dated || bookingsArr.length === 0) return null;
      const ts = typeof dated === 'number' ? dated : new Date(dated).getTime();
      if (isNaN(ts)) return null;
      // Restrict to my bookings
      const mine = bookingsArr.filter(b =>
        (myRole === 'customer' && b.customer === myUid) ||
        (myRole === 'fleetadmin' && b.fleetadmin === myUid) ||
        (myRole === 'admin')
      );
      // Find status keyword match
      const kw = statusKeywords.find(k => k.re.test(haystack));
      if (!kw) return null;
      // Score by status hit + temporal proximity (within 24h preferred, fallback 7d)
      const candidates = mine
        .map(b => {
          const bts = b.bookingDate ? new Date(b.bookingDate).getTime() : 0;
          const dist = bts ? Math.abs(bts - ts) : Infinity;
          const statusMatch = kw.target.includes(b.status);
          return { b, dist, statusMatch };
        })
        .filter(c => c.dist < 7 * 86400000)
        .sort((a, b) => {
          if (a.statusMatch !== b.statusMatch) return a.statusMatch ? -1 : 1;
          return a.dist - b.dist;
        });
      return candidates.length ? candidates[0].b : null;
    };

    const formatWhen = (ts) => {
      if (!ts) return '';
      const d = new Date(typeof ts === 'number' || /^\d+$/.test(String(ts)) ? Number(ts) : ts);
      if (isNaN(d.getTime())) return '';
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      const pad = (n) => String(n).padStart(2, '0');
      const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      if (sameDay) return `Oggi ${time}`;
      const months = ['gen','feb','mar','apr','mag','giu','lug','ago','set','ott','nov','dic'];
      return `${d.getDate()} ${months[d.getMonth()]} ${time}`;
    };

    // 1. Push admin notifications (already in Redux)
    (notificationdata?.notifications || []).forEach(n => {
      const msg = (n.msg || n.text || n.body || '').trim();
      const titleField = (n.title || '').trim();
      const isGenericTitle = !titleField || /^(hai una notifica|you have a notification)$/i.test(titleField);

      const linkedBooking = findLinkedBooking(msg, titleField, n.dated || n.dateString || n.createdAt);
      const refResolved = linkedBooking ? (linkedBooking.reference || (linkedBooking.id ? linkedBooking.id.slice(-6).toUpperCase() : null)) : null;
      const dName = linkedBooking
        ? (linkedBooking.driver_name || (linkedBooking.driver && driverNameById[linkedBooking.driver]) || '')
        : '';

      // Build a clean message: strip the awkward "ID di prenotazione èREF" tail if present
      const cleanMsg = msg
        .replace(/\bil tuo id di prenotazione[\s\S]*$/i, '')
        .replace(/\bbooking id[\s\S]*$/i, '')
        // Replace generic "il conducente"/"il trasportatore" with the real name
        .replace(/\bil\s+(conducente|trasportatore|driver)\b/gi, dName ? `il trasportatore ${dName}` : 'il trasportatore')
        .replace(/[.\s]+$/, '')
        .trim();

      const finalTitle = (isGenericTitle && cleanMsg) ? cleanMsg : (titleField || cleanMsg || 'Notifica di sistema');

      // Subtitle = "Trasportatore: Nome • #REF • Oggi 14:32"
      const parts = [];
      if (dName) parts.push(`Trasportatore: ${dName}`);
      if (refResolved) parts.push(`#${refResolved}`);
      const when = formatWhen(n.dated || n.dateString || n.createdAt);
      if (when) parts.push(when);
      const finalSubtitle = parts.join(' • ');

      items.push({
        id: `push:${n.id}`,
        icon: 'push',
        title: finalTitle,
        subtitle: finalSubtitle,
        timestamp: n.dated || n.dateString || n.createdAt || '',
        route: linkedBooking ? `/bookings/bookingdetails/${linkedBooking.id}` : '/facilitynotifications',
        isRead: n.isRead === true || acknowledged.has(`push:${n.id}`) || isReadByTime(n.dated || n.dateString || n.createdAt),
      });
    });

    // 2. Booking-derived notifications (only for customer + fleetadmin)
    if (myRole === 'customer' || myRole === 'fleetadmin') {
      (bookinglistdata?.bookings || []).forEach(b => {
        const isMine =
          (myRole === 'customer' && b.customer === myUid) ||
          (myRole === 'fleetadmin' && b.fleetadmin === myUid);
        if (!isMine) return;

        // 2a. New bid offers — when a driver proposed a price
        if (b.status === 'NEW' && b.driverOffers && typeof b.driverOffers === 'object') {
          Object.keys(b.driverOffers).forEach(driverId => {
            const offer = b.driverOffers[driverId];
            const dName = driverNameById[driverId] || 'Trasportatore';
            const itemId = `bid:${b.id}:${driverId}`;
            const ts = offer.timestamp || b.bookingDate || '';
            const when = formatWhen(ts);
            const subParts = [`${dName} — ${formatCost(offer.trip_cost || offer.amount || offer)}`];
            if (when) subParts.push(when);
            items.push({
              id: itemId,
              icon: 'bid',
              title: `Nuova offerta ${refLabel(b)}`,
              subtitle: subParts.join(' • '),
              timestamp: ts,
              route: `/bookings/bookingdetails/${b.id}`,
              isRead: acknowledged.has(itemId) || isReadByTime(ts),
            });
          });
        }

        // 2b. Recent state changes (ACCEPTED, ARRIVED, STARTED, REACHED)
        if (['ACCEPTED', 'ARRIVED', 'STARTED', 'REACHED'].includes(b.status)) {
          const dName = b.driver_name || (b.driver && driverNameById[b.driver]) || '';
          const statusLabel = {
            ACCEPTED: 'Prenotazione accettata',
            ARRIVED: 'Trasportatore arrivato',
            STARTED: 'Viaggio iniziato',
            REACHED: 'Destinazione raggiunta',
          }[b.status] || b.status;
          const itemId = `status:${b.id}:${b.status}`;
          const when = formatWhen(b.bookingDate);
          const subParts = [];
          if (dName) subParts.push(`Trasportatore: ${dName}`);
          if (when) subParts.push(when);
          items.push({
            id: itemId,
            icon: 'status',
            title: `${refLabel(b)} — ${statusLabel}`,
            subtitle: subParts.join(' • ') || 'Stato aggiornato',
            timestamp: b.bookingDate || '',
            route: `/bookings/bookingdetails/${b.id}`,
            isRead: acknowledged.has(itemId) || isReadByTime(b.bookingDate),
          });
        }

        // 2c. Pending reviews
        if (myRole === 'customer' && b.status === 'COMPLETE' && !b.rating && !dismissedReviews.has(b.id)) {
          const dName = b.driver_name || (b.driver && driverNameById[b.driver]) || '';
          const itemId = `review:${b.id}`;
          const when = formatWhen(b.bookingDate);
          const subParts = [];
          if (dName) subParts.push(`Trasportatore: ${dName}`);
          if (when) subParts.push(when);
          items.push({
            id: itemId,
            icon: 'review',
            title: `Lascia recensione ${refLabel(b)}`,
            subtitle: subParts.join(' • '),
            timestamp: b.bookingDate || '',
            route: `/bookings`,
            isRead: false,
          });
        }
      });
    }

    items.sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    });
    return items;
  }, [notificationdata?.notifications, bookinglistdata?.bookings, auth?.profile?.uid, auth?.profile?.usertype, settings?.symbol, settings?.swipe_symbol, driverNameById, seenBellIds, dismissedUntil, t]);

  const unreadBellCount = bellNotifications.filter(n => !n.isRead).length;

  const handleBellClick = (e) => setBellAnchor(e.currentTarget);
  const handleBellClose = () => setBellAnchor(null);

  const persistSeen = (set) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('medlift_bell_seen', Array.from(set).join('|'));
    }
  };

  const handleNotificationClick = (n) => {
    setSeenBellIds(prev => {
      const next = new Set(prev);
      next.add(n.id);
      persistSeen(next);
      return next;
    });
    handleBellClose();
    if (n.route) navigate(n.route);
  };

  const handleMarkAllRead = () => {
    const now = Date.now();
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('medlift_bell_dismissed_until', String(now));
    }
    setDismissedUntil(now);
    // Also persist the per-id seen set so individual items show as read even
    // if their timestamp is missing
    setSeenBellIds(prev => {
      const next = new Set(prev);
      bellNotifications.forEach(n => next.add(n.id));
      persistSeen(next);
      return next;
    });
  };

  useEffect(() => {
    setNotificationCount(unreadBellCount);
  }, [unreadBellCount]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDrawer = () => setMobileOpen(false);
  const LogOut = () => dispatch(signOff());

  const navItems = [
    { name: t('home'), url: '/home', icon: <HomeIcon />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
    { name: t('dashboard_text'), url: '/dashboard', icon: <DashboardIcon />, access: ['admin', 'fleetadmin'] },
    { name: t('booking_history'), url: '/bookings', icon: <ViewListIcon />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
    { name: t('drivers'), url: '/drivers', icon: <CarIcon />, access: ['customer'] },
    { name: t('messages'), url: '/chats', icon: <Message />, access: ['admin', 'fleetadmin', 'customer'] },
    {
      name: t('addbookinglable'),
      url: '/addbookings',
      icon: <ContactPhoneIcon />,
      access: calcEst && !showEst && !optionsRequired ? ['customer'] : ['admin', 'fleetadmin', 'customer'],
    },
    { name: t('user'), url: '/users/0', icon: <EmojiPeopleIcon />, access: ['admin', 'fleetadmin'] },
    { name: t('car_type'), url: '/cartypes', icon: <CarIcon />, access: ['admin'] },
    { name: t('cars'), url: '/cars', icon: <CarIcon />, access: ['admin', 'fleetadmin', 'driver'] },
    { name: t('withdraws_web'), url: '/withdraws', icon: <MoneyIcon />, access: ['admin'] },
    { name: t('report'), url: '/allreports', icon: <AssessmentIcon />, access: ['admin', 'fleetadmin'] },
    { name: t('promo'), url: '/promos', icon: <OfferIcon />, access: ['admin'] },
    { name: t('push_notifications'), url: '/notifications', icon: <NotifyIcon />, access: ['admin'] },
    { name: t('push_notifications'), url: '/facilitynotifications', icon: <NotifyIcon />, access: ['customer'], count: notificationCount },
    { name: 'Pagamenti Trasportatori', url: '/payouts', icon: <MoneyIcon />, access: ['customer'] },
    { name: t('sos'), url: '/sos', icon: <SosIcon />, access: ['admin'] },
    { name: t('complain'), url: '/complain', icon: <AnnouncementOutlinedIcon />, access: ['admin'] },
    { name: t('my_wallet_tile'), url: '/userwallet', icon: <AccountBalanceWalletIcon />, access: ['driver', 'customer', 'fleetadmin'] },
    { name: t('settings_title'), url: '/settings', icon: <PhoneIphoneIcon />, access: ['admin'] },
    { name: t('payment_settings'), url: '/paymentsettings', icon: <PhoneIphoneIcon />, access: ['admin'] },
    { name: t('profile'), url: '/profile', icon: <AccountCircleIcon />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
  ];

  const isActive = (url) => {
    if (!url) return false;
    if (url === '/home') return location.pathname === '/home' || location.pathname === '/';
    if (url === '/users/0') return location.pathname.startsWith('/users');
    return location.pathname === url || location.pathname.startsWith(url + '/');
  };

  const userInitials = (() => {
    const f = (auth?.profile?.firstName || '').trim();
    const l = (auth?.profile?.lastName || '').trim();
    if (f && l) return (f[0] + l[0]).toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    if (auth?.profile?.email) return auth.profile.email.slice(0, 2).toUpperCase();
    return 'M';
  })();

  const roleLabel = ({
    admin: 'Admin',
    fleetadmin: 'Fleet Admin',
    customer: 'Struttura',
    driver: 'Trasportatore',
  })[role] || '';

  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: BRAND.slate950,
        fontFamily: BRAND.font,
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Brand header */}
      <Box
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderBottom: `1px solid rgba(255,255,255,0.06)`,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/landing/Images/Frame 45.svg"
          alt="MedLift"
          sx={{ width: 36, height: 36, borderRadius: '10px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <Box>
          <Typography
            sx={{
              fontFamily: BRAND.font,
              fontWeight: 700,
              fontSize: 17,
              letterSpacing: '-0.025em',
              color: '#fff',
              lineHeight: 1,
            }}
          >
            {settings?.appName || 'MedLift'}
          </Typography>
          <Typography
            sx={{
              fontFamily: BRAND.font,
              fontSize: 11,
              color: BRAND.slate400,
              mt: 0.25,
            }}
          >
            Trasporto sanitario
          </Typography>
        </Box>
      </Box>

      {/* User identity card */}
      <Box
        sx={{
          mx: 2,
          mt: 2,
          mb: 1.5,
          p: 1.5,
          borderRadius: '14px',
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.tealDark} 100%)`,
            color: BRAND.slate900,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: BRAND.font,
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: '-0.02em',
            flexShrink: 0,
          }}
        >
          {userInitials}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: BRAND.font,
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {(auth?.profile?.firstName || '') + (auth?.profile?.lastName ? ' ' + auth.profile.lastName : '') || 'User'}
          </Typography>
          {roleLabel && (
            <Typography
              sx={{
                fontFamily: BRAND.font,
                fontSize: 10.5,
                color: BRAND.teal,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                mt: 0.25,
              }}
            >
              {roleLabel}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Nav items (scrollable) */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          py: 1,
          // Custom scrollbar in dark sidebar
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.08)',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255,255,255,0.16)',
          },
        }}
      >
        {role && navItems.filter(i => i.access.includes(role)).map((item, idx) => {
          const active = isActive(item.url);
          return (
            <Box
              key={`${item.url}-${idx}`}
              component={Link}
              to={item.url}
              onClick={handleDrawer}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                mx: 0.5,
                my: 0.25,
                borderRadius: '10px',
                color: active ? BRAND.teal : BRAND.slate300,
                backgroundColor: active ? BRAND.tealLight : 'transparent',
                textDecoration: 'none',
                fontFamily: BRAND.font,
                fontSize: 14,
                fontWeight: active ? 600 : 500,
                position: 'relative',
                transition: 'all 180ms cubic-bezier(0.4,0,0.2,1)',
                '&:hover': {
                  backgroundColor: active ? BRAND.tealLight : 'rgba(255,255,255,0.05)',
                  color: active ? BRAND.teal : '#fff',
                },
                '&::before': active
                  ? {
                      content: '""',
                      position: 'absolute',
                      left: -8,
                      top: 8,
                      bottom: 8,
                      width: 3,
                      borderRadius: '0 4px 4px 0',
                      backgroundColor: BRAND.teal,
                    }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiSvgIcon-root': { fontSize: 20 },
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ flex: 1, lineHeight: 1.2, textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>{item.name}</Box>
              {item.count > 0 && (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    borderRadius: '999px',
                    backgroundColor: BRAND.teal,
                    color: BRAND.slate900,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: BRAND.font,
                  }}
                >
                  {item.count}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Logout pinned bottom */}
      <Box
        sx={{
          flexShrink: 0,
          p: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={LogOut}
          sx={{
            cursor: 'pointer',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 1.25,
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: 'transparent',
            color: BRAND.slate300,
            fontFamily: BRAND.font,
            fontSize: 13,
            fontWeight: 500,
            transition: 'all 180ms cubic-bezier(0.4,0,0.2,1)',
            '&:hover': {
              borderColor: 'rgba(244,63,94,0.4)',
              color: '#f43f5e',
              backgroundColor: 'rgba(244,63,94,0.05)',
            },
          }}
        >
          <ExitIcon sx={{ fontSize: 18 }} />
          <Box sx={{ flex: 1, textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>{t('logout')}</Box>
        </Box>
      </Box>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;
  const isCustomerMobile = role === 'customer' && isMobile;

  return (
    <Box sx={{ display: 'flex', direction: isRTL === 'rtl' ? 'rtl' : 'ltr', backgroundColor: BRAND.slate50, minHeight: '100vh' }}>
      <CssBaseline />

      {/* Top AppBar — light surface with subtle border */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: isRTL === 'rtl' ? 0 : { md: `${drawerWidth}px` },
          mr: isRTL === 'rtl' ? { md: `${drawerWidth}px` } : 0,
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${BRAND.slate200}`,
          color: BRAND.slate900,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, md: 64 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 1.5,
              display: { md: 'none' },
              color: BRAND.slate900,
              borderRadius: '10px',
              '&:hover': { backgroundColor: BRAND.slate100 },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            noWrap
            sx={{
              fontFamily: BRAND.font,
              fontSize: 16,
              fontWeight: 700,
              color: BRAND.slate900,
              letterSpacing: '-0.015em',
            }}
          >
            {settings && settings.appName ? settings.appName : 'MedLift'}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <IconButton
            aria-label="notifications"
            onClick={handleBellClick}
            sx={{
              color: BRAND.slate900,
              borderRadius: '10px',
              '&:hover': { backgroundColor: BRAND.slate100 },
            }}
          >
            <Badge
              badgeContent={unreadBellCount}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontFamily: BRAND.font,
                  fontWeight: 700,
                  fontSize: 10,
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                },
              }}
            >
              <NotificationsNoneIcon />
            </Badge>
          </IconButton>

          <Popover
            open={Boolean(bellAnchor)}
            anchorEl={bellAnchor}
            onClose={handleBellClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                width: { xs: 320, sm: 380 },
                maxHeight: 480,
                mt: 1,
                borderRadius: '14px',
                boxShadow: '0 12px 32px rgba(15,23,42,0.18), 0 2px 8px rgba(15,23,42,0.06)',
                border: `1px solid ${BRAND.slate200}`,
                fontFamily: BRAND.font,
                overflow: 'hidden',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${BRAND.slate100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontFamily: BRAND.font, fontWeight: 700, fontSize: 14, color: BRAND.slate900 }}>
                Notifiche
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {unreadBellCount > 0 && (
                  <Box sx={{ fontFamily: BRAND.font, fontSize: 11, fontWeight: 600, color: BRAND.teal, backgroundColor: BRAND.tealLight, px: 1, py: 0.25, borderRadius: '999px' }}>
                    {unreadBellCount} nuove
                  </Box>
                )}
                {bellNotifications.length > 0 && unreadBellCount > 0 && (
                  <Box
                    component="button"
                    onClick={handleMarkAllRead}
                    sx={{
                      fontFamily: BRAND.font,
                      fontSize: 11,
                      fontWeight: 600,
                      color: BRAND.slate500,
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                      '&:hover': { color: BRAND.slate900 },
                    }}
                  >
                    Segna tutte lette
                  </Box>
                )}
              </Box>
            </Box>
            {bellNotifications.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', fontFamily: BRAND.font, color: BRAND.slate500, fontSize: 13 }}>
                Nessuna notifica
              </Box>
            ) : (
              <List dense sx={{ p: 0, overflowY: 'auto', maxHeight: 400 }}>
                {bellNotifications.map((n, idx) => {
                  const Icon = n.icon === 'bid' ? GavelIcon
                    : n.icon === 'status' ? EventNoteIcon
                    : n.icon === 'review' ? StarBorderIcon
                    : CampaignIcon;
                  const iconColor = n.icon === 'bid' ? '#7c3aed'
                    : n.icon === 'status' ? '#0ea5e9'
                    : n.icon === 'review' ? '#f59e0b'
                    : BRAND.teal;
                  return (
                    <React.Fragment key={n.id}>
                      <ListItemButton
                        onClick={() => handleNotificationClick(n)}
                        sx={{
                          px: 2,
                          py: 1.25,
                          alignItems: 'flex-start',
                          backgroundColor: n.isRead ? 'transparent' : 'rgba(79,209,197,0.06)',
                          '&:hover': { backgroundColor: BRAND.slate50 },
                        }}
                      >
                        <Box sx={{ width: 32, height: 32, borderRadius: '10px', backgroundColor: `${iconColor}15`, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>
                          <Icon sx={{ fontSize: 18 }} />
                        </Box>
                        <ListItemText
                          disableTypography
                          primary={
                            <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, fontWeight: n.isRead ? 500 : 700, color: BRAND.slate900, lineHeight: 1.3 }}>
                              {n.title}
                            </Typography>
                          }
                          secondary={
                            <Typography sx={{ fontFamily: BRAND.font, fontSize: 12, color: BRAND.slate500, mt: 0.25, lineHeight: 1.3 }}>
                              {n.subtitle}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                      {idx < bellNotifications.length - 1 && <Divider sx={{ borderColor: BRAND.slate100 }} />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Popover>
        </Toolbar>
      </AppBar>

      {/* Drawer (mobile + permanent desktop) */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              backgroundColor: BRAND.slate950,
            },
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              backgroundColor: BRAND.slate950,
              boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.04)',
            },
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          paddingBottom: isCustomerMobile ? '72px' : undefined,
          backgroundColor: BRAND.slate50,
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {props.children}
      </Box>

      {isCustomerMobile && <BottomNav />}
    </Box>
  );
}
